import { isDesktop } from '@/platform';

/**
 * 前端與 Rust 之間的橋（desktop-app.md 第 9 節）。
 *
 * 所有 Tauri API 一律 `await import()`，這個檔案本身不 import 任何 Tauri 套件——
 * 網頁版的 initial bundle 不會把它們帶進去。網頁版呼叫這裡的每個函式都會得到
 * null 或 no-op，呼叫端不必到處寫 `if (isDesktop())`。
 */

export interface MirrorChange {
  table: string;
  id: string;
  json?: string;
  blobBase64?: string;
  thumbnailBase64?: string;
  deleted?: boolean;
}

export interface MirrorStatus {
  path: string;
  exists: boolean;
  byteSize: number;
  schemaVersion: number;
  lastSyncAt: string | null;
  rowCount: number;
}

export interface MirrorSnapshotAttachment {
  json: string;
  blobBase64: string;
  thumbnailBase64: string;
}

export interface MirrorSnapshot {
  tables: Record<string, string[]>;
  attachments: MirrorSnapshotAttachment[];
}

type Invoke = <T>(command: string, args?: Record<string, unknown>) => Promise<T>;

let invokeCache: Invoke | null = null;

async function getInvoke(): Promise<Invoke | null> {
  if (!isDesktop()) return null;
  if (invokeCache) return invokeCache;
  const core = await import('@tauri-apps/api/core');
  invokeCache = core.invoke as Invoke;
  return invokeCache;
}

export async function mirrorStatus(): Promise<MirrorStatus | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<MirrorStatus>('mirror_status');
}

export async function mirrorApply(changes: MirrorChange[], at: string): Promise<number> {
  const invoke = await getInvoke();
  if (!invoke || changes.length === 0) return 0;
  return invoke<number>('mirror_apply', { changes, at });
}

/** 匯入的 replace-all 一定要走這條，逐筆同步會留下舊資料殘骸（6.4）。 */
export async function mirrorRebuild(changes: MirrorChange[], at: string): Promise<number> {
  const invoke = await getInvoke();
  if (!invoke) return 0;
  return invoke<number>('mirror_rebuild', { changes, at });
}

export async function mirrorSnapshot(): Promise<MirrorSnapshot | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<MirrorSnapshot>('mirror_snapshot');
}

export async function mirrorReadFile(path: string): Promise<MirrorSnapshot | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<MirrorSnapshot>('mirror_read_file', { path });
}

export async function mirrorBackup(stamp: string): Promise<string | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<string>('mirror_backup', { stamp });
}

export async function mirrorExport(destination: string): Promise<string | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<string>('mirror_export', { destination });
}

export async function mirrorDataDir(): Promise<string | null> {
  const invoke = await getInvoke();
  if (!invoke) return null;
  return invoke<string>('mirror_data_dir');
}

/** 在檔案總管裡打開「文件\GentleTask」。 */
export async function openDataFolder(): Promise<boolean> {
  if (!isDesktop()) return false;
  const path = await mirrorDataDir();
  if (!path) return false;
  const opener = await import('@tauri-apps/plugin-opener');
  await opener.openPath(path);
  return true;
}

/** 「匯出備份到…」的存檔對話框。取消時回 null。 */
export async function pickBackupDestination(defaultName: string): Promise<string | null> {
  if (!isDesktop()) return null;
  const dialog = await import('@tauri-apps/plugin-dialog');
  const chosen = await dialog.save({
    defaultPath: defaultName,
    filters: [{ name: 'GentleTask 完整備份', extensions: ['zip'] }],
  });
  return chosen ?? null;
}

/** 「從 .db 還原」的開檔對話框。取消時回 null。 */
export async function pickRestoreSource(): Promise<string | null> {
  if (!isDesktop()) return null;
  const dialog = await import('@tauri-apps/plugin-dialog');
  const chosen = await dialog.open({
    multiple: false,
    directory: false,
    filters: [{ name: 'GentleTask 備份（ZIP／舊版 DB）', extensions: ['zip', 'db'] }],
  });
  return typeof chosen === 'string' ? chosen : null;
}

/**
 * 關窗攔截（10.6）。
 *
 * Rust 那邊已經 `prevent_close()` 並發出事件，這裡把還在 debounce 的內容寫出去，
 * 再回報 `confirm_close` 讓視窗真的關掉。Rust 有 15 秒的保險，
 * 這裡卡住不會讓使用者關不掉程式。
 */
export async function attachDesktopCloseFlush(flush: () => Promise<void>): Promise<() => void> {
  if (!isDesktop()) return () => {};

  const [{ listen }, core] = await Promise.all([
    import('@tauri-apps/api/event'),
    import('@tauri-apps/api/core'),
  ]);

  const unlisten = await listen('gentletask://close-requested', () => {
    void flush()
      .catch(() => {
        // 寫不出去也要讓視窗關得掉；內容留在 pending，下次開啟時草稿還在。
      })
      .finally(() => {
        void core.invoke('confirm_close');
      });
  });

  return unlisten;
}
